/**
 * Central registry for `window` `canvas:*` custom events (open entity in pane, edge style,
 * explorer reparent, node highlight, etc.). Keeps listeners stable via refs; called from
 * `CanvasPageInner` once per mount (under `CanvasProcessProvider`).
 */

import { useRegisterCanvasReparentHandler } from "@/dnd/TransferDndProvider";
import type { Entity } from "@cohesive/ui-components";
import { useReactFlow } from "@xyflow/react";
import { useCallback, useEffect, useRef } from "react";
import { CANVAS_SNAP, CANVAS_ZOOM } from "../canvas-controls.config";
import {
  CANVAS_EXPAND_NODE_EVENT,
  type CanvasExpandNodeDetail,
} from "../constants/canvasExpandNode.constants";
import {
  CANVAS_FOCUS_NODES_EVENT,
  type CanvasFocusNodesDetail,
  selectNavigableNodeIds,
} from "../constants/canvasFocusNodes.constants";
import { useCanvas } from "../context/CanvasContext";
import type { CanvasFloatingSessionHistory } from "../context/CanvasFloatingChatContext";
import { useCanvasProcess } from "../context/CanvasProcessContext";
import type { EdgeStyle } from "../nodes/LineFloatingToolbar";
import {
  buildDetachUpdatesForSectionRemovals,
  buildExplorerReparentUpdates,
} from "../utils/sectionMembership";
import { useCanvasPermissions } from "./useCanvasPermissions";

/** Dispatched (e.g. from artifact pane) so the canvas page opens the floating agent chat if it is closed. */
export const CANVAS_OPEN_CHAT_EVENT = "canvas:open-chat";

export function emitCanvasOpenChat(detail: {
  canvasId: string;
  /**
   * Prompt to prefill in the composer of whichever session the chat lands on
   * (e.g. "Create with Agent"). The canvas resolves that session, so seeding
   * it is its job rather than the caller's.
   */
  composerDraft?: string;
}) {
  window.dispatchEvent(new CustomEvent(CANVAS_OPEN_CHAT_EVENT, { detail }));
}

/** Dispatched (e.g. from an onboarding step) to open the Add Context popover. */
export const CANVAS_OPEN_ADD_CONTEXT_EVENT = "canvas:open-add-context";

export function emitCanvasOpenAddContext() {
  window.dispatchEvent(new CustomEvent(CANVAS_OPEN_ADD_CONTEXT_EVENT));
}

/** Dispatched (e.g. from connectors rail) to add an entity node to the active canvas. */
export const CANVAS_ADD_ENTITY_EVENT = "canvas:add-entity";

export function emitCanvasAddEntity(entity: Entity) {
  window.dispatchEvent(
    new CustomEvent(CANVAS_ADD_ENTITY_EVENT, { detail: { entity } }),
  );
}

/**
 * Dispatched after the canvas's one-shot initial `fitView` completes.
 * E2E listens for this (and the durable `window` flag) before interacting.
 */
export const CANVAS_INITIAL_FIT_VIEW_EVENT = "canvas:initial-fit-view";

const CANVAS_INITIAL_FIT_VIEW_FLAG = "__canvasInitialFitView";

type CanvasInitialFitViewFlag = { canvasId: string };

export function emitCanvasInitialFitView(detail: CanvasInitialFitViewFlag) {
  (
    window as Window & {
      [CANVAS_INITIAL_FIT_VIEW_FLAG]?: CanvasInitialFitViewFlag;
    }
  )[CANVAS_INITIAL_FIT_VIEW_FLAG] = detail;
  window.dispatchEvent(
    new CustomEvent(CANVAS_INITIAL_FIT_VIEW_EVENT, { detail }),
  );
}

export function clearCanvasInitialFitViewSignal() {
  delete (
    window as Window & {
      [CANVAS_INITIAL_FIT_VIEW_FLAG]?: CanvasInitialFitViewFlag;
    }
  )[CANVAS_INITIAL_FIT_VIEW_FLAG];
}

export interface UseCanvasWindowEventsOptions {
  /** Active workspace id — session opens are ignored when the event targets another canvas. */
  canvasId: string | undefined;
  openCanvasFloatingSession: (
    sessionId: string,
    history?: CanvasFloatingSessionHistory,
  ) => void;
  handleEdgeStyleChange: (edgeId: string, style: EdgeStyle) => void;
  /** Expands the given chat session node's quick chat panel. */
  openChat: (id: string) => void;
  handleSelectEntityRef: React.RefObject<((entity: Entity) => void) | null>;
  /** Optional — called when a widget requests a Quick Chat with pre-filled content. */
  handleStartQuickChat?: (
    screenPos: { x: number; y: number },
    initialContent?: string,
  ) => void;
  /** Opens floating agent chat (existing main session or new) when {@link emitCanvasOpenChat} fires. */
  ensureCanvasAgentChatOpen?: (composerDraft?: string) => Promise<void>;
}

/**
 * Registers all canvas: custom window event listeners in one place.
 * Uses refs so listeners are registered once and always call the latest callbacks.
 */
export function useCanvasWindowEvents({
  canvasId,
  openCanvasFloatingSession,
  handleEdgeStyleChange,
  openChat,
  handleSelectEntityRef,
  handleStartQuickChat,
  ensureCanvasAgentChatOpen,
}: UseCanvasWindowEventsOptions) {
  const canvas = useCanvas();
  const { setHighlightedNodeIds } = useCanvasProcess();
  const { canEdit } = useCanvasPermissions();
  const reactFlowInstance = useReactFlow();
  const refs = useRef({
    canEdit,
    reactFlowInstance,
    canvas,
    handleEdgeStyleChange,
    openChat,
    handleSelectEntityRef,
    canvasId,
    openCanvasFloatingSession,
    setHighlightedNodeIds,
    handleStartQuickChat,
    ensureCanvasAgentChatOpen,
  });
  refs.current = {
    canEdit,
    reactFlowInstance,
    canvas,
    handleEdgeStyleChange,
    openChat,
    handleSelectEntityRef,
    canvasId,
    openCanvasFloatingSession,
    setHighlightedNodeIds,
    handleStartQuickChat,
    ensureCanvasAgentChatOpen,
  };

  const handleReparentNodes = useCallback(
    ({
      canvasId: eventCanvasId,
      nodeIds,
      targetParentSectionId,
    }: {
      canvasId: string;
      nodeIds: string[];
      targetParentSectionId: string | null;
    }) => {
      const r = refs.current;
      if (!r.canEdit) return;
      if (eventCanvasId !== r.canvasId) return;
      if (nodeIds.length === 0) return;

      const rf = r.reactFlowInstance;
      const l = r.canvas;
      const targetParentId = targetParentSectionId;
      const nodeMap = new Map(rf.getNodes().map((n) => [n.id, n]));

      let targetSectionAbsPos: { x: number; y: number } | undefined;
      if (targetParentId !== null) {
        const targetSection = nodeMap.get(targetParentId);
        if (!targetSection || targetSection.type !== "section") return;
        const internal = rf.getInternalNode(targetParentId);
        targetSectionAbsPos =
          internal?.internals.positionAbsolute ?? targetSection.position;
      }

      const [snapX, snapY] = CANVAS_SNAP.grid;
      const updates = buildExplorerReparentUpdates({
        nodeIds,
        getNode: (id) => nodeMap.get(id),
        getInternalNode: (id) => rf.getInternalNode(id),
        targetParentSectionId: targetParentId,
        targetSectionAbsPos,
        snap: (v, step) => Math.round(v / step) * step,
        snapX,
        snapY,
      });
      if (updates.length === 0) return;
      l.batchUpdateNodeParents(updates);
    },
    [],
  );

  useRegisterCanvasReparentHandler(handleReparentNodes);

  useEffect(() => {
    const r = () => refs.current;

    const onOpenSessionPane = (e: Event) => {
      const {
        flowSessionId,
        canvasId: cid,
        history,
      } = (e as CustomEvent).detail as {
        flowSessionId?: string;
        canvasId?: string;
        history?: CanvasFloatingSessionHistory;
      };
      if (!cid || !flowSessionId) return;
      if (cid !== r().canvasId) return;
      if (history === "replace") {
        r().openCanvasFloatingSession(flowSessionId, "replace");
      } else {
        r().openCanvasFloatingSession(flowSessionId);
      }
    };

    const onDeleteSection = (e: Event) => {
      const { canEdit: ce, reactFlowInstance: rf, canvas: l } = r();
      if (!ce) return;
      const { sectionId } = (e as CustomEvent).detail as { sectionId: string };
      if (!sectionId) return;
      const allNodes = rf.getNodes();
      const detach = buildDetachUpdatesForSectionRemovals(
        [sectionId],
        allNodes,
        rf.getInternalNode,
      );
      if (detach.length > 0) {
        l.batchUpdateNodeParents(detach);
      }
      l.removeNodes([sectionId]);
    };

    const onEdgeLabelChange = (e: Event) => {
      const { edgeId, label } = (e as CustomEvent).detail as {
        edgeId: string;
        label: string;
      };
      if (!r().canEdit) return;
      r().canvas.updateEdge(edgeId, { label });
    };

    const onRemoveNode = (e: Event) => {
      const { canEdit: ce, canvas: l } = r();
      if (!ce) return;
      // `origin` is an opaque tag from the dispatcher. The `"no-undo:"`
      // prefix marks deletions that should NOT enter the Liveblocks
      // undo/redo stack — used by file-record deletion, where the node
      // removal accompanies an irreversible API call. Letting the user
      // "undo" it would resurrect a phantom node pointing at a deleted
      // record. We route those through `withoutUndo`, a thin wrapper
      // around `room.history.disable(fn)` exposed by the canvas context.
      const { nodeId, origin } = (e as CustomEvent).detail as {
        nodeId: string;
        origin?: string;
      };
      if (!nodeId) return;
      if (origin?.startsWith("no-undo:")) {
        l.withoutUndo(() => l.removeNode(nodeId));
      } else {
        l.removeNode(nodeId);
      }
    };

    // Hide/show a node on the surface (non-destructive counterpart to
    // canvas:remove-node) — dispatched by rail rows, which live outside the
    // canvas context.
    const onSetNodeHidden = (e: Event) => {
      const { canEdit: ce, canvas: l } = r();
      if (!ce) return;
      const { nodeId, hidden } = (e as CustomEvent).detail as {
        nodeId: string;
        hidden: boolean;
      };
      if (!nodeId) return;
      l.setNodeHidden(nodeId, hidden);
    };

    const onFocusNode = (e: Event) => {
      const { reactFlowInstance: rf } = r();
      const { nodeId, keepZoom } = (e as CustomEvent).detail as {
        nodeId: string;
        /** When true, preserve the current zoom level instead of
         *  capping it at 1.0. Used by sidebar "Locate" actions where
         *  the user explicitly wants to find a node without losing
         *  their current detail level. */
        keepZoom?: boolean;
      };
      if (!nodeId) return;
      const node = rf.getNode(nodeId);
      if (!node) return;

      const absPos =
        (node as any).internals?.positionAbsolute ??
        (() => {
          let ax = node.position.x;
          let ay = node.position.y;
          let cur = node;
          while (cur.parentId) {
            const p = rf.getNode(cur.parentId);
            if (!p) break;
            ax += p.position.x;
            ay += p.position.y;
            cur = p;
          }
          return { x: ax, y: ay };
        })();
      const absX = absPos.x;
      const absY = absPos.y;

      const w = (node.measured?.width ?? node.width ?? 200) as number;
      const h = (node.measured?.height ?? node.height ?? 100) as number;
      const currentZoom = rf.getZoom();
      const targetZoom = keepZoom ? currentZoom : Math.min(currentZoom, 1);
      rf.setCenter(absX + w / 2, absY + h / 2, {
        zoom: targetZoom,
        duration: 400,
      });
      rf.setNodes((nds) =>
        nds.map((n) => ({ ...n, selected: n.id === nodeId })),
      );
    };

    // A pin's authoritative position is thread metadata, not a node:
    // `canvas:focus-node` hard-returns without a nodeId and ends by selecting
    // the node it found, neither of which fits a comment.
    const onFocusPoint = (e: Event) => {
      const { reactFlowInstance: rf } = r();
      const { x, y, keepZoom } = (e as CustomEvent).detail as {
        x: number;
        y: number;
        keepZoom?: boolean;
      };
      if (typeof x !== "number" || typeof y !== "number") return;
      const currentZoom = rf.getZoom();
      rf.setCenter(x, y, {
        zoom: keepZoom ? currentZoom : Math.min(currentZoom, 1),
        duration: 400,
      });
    };

    // Fit the viewport to a set of nodes (e.g. everything one agent turn
    // added). Requested ids are narrowed to the ones fitView will actually
    // move for — present, not hidden, and measured — so nodes deleted or
    // hidden since the edit degrade gracefully instead of erroring. If none
    // are navigable we leave the event uncancelled and the caller learns the
    // navigation did not happen.
    const onFocusNodes = (e: Event) => {
      const { reactFlowInstance: rf } = r();
      const { nodeIds } = (e as CustomEvent)
        .detail as Partial<CanvasFocusNodesDetail>;
      if (!nodeIds?.length) return;
      const navigable = selectNavigableNodeIds(nodeIds, rf.getNodes());
      if (navigable.length === 0) return;
      const matched = new Set(navigable);
      e.preventDefault(); // ack: a canvas handled this
      void rf.fitView({
        nodes: navigable.map((id) => ({ id })),
        minZoom: CANVAS_ZOOM.minZoom,
        maxZoom: 1,
        duration: 400,
      });
      rf.setNodes((nds) =>
        nds.map((n) => ({ ...n, selected: matched.has(n.id) })),
      );
    };

    // Open a block full-size (the lightbox) from outside the canvas context —
    // the detail pane's Expand routes back here with the node it was opened
    // from. Acked only when that node is on this canvas.
    const onExpandNode = (e: Event) => {
      const { reactFlowInstance: rf, canvas: l } = r();
      const { nodeId } = (e as CustomEvent)
        .detail as Partial<CanvasExpandNodeDetail>;
      if (!nodeId || !rf.getNode(nodeId)) return;
      e.preventDefault(); // ack: a canvas handled this
      l.expandNode(nodeId);
    };

    const onSelectNodes = (e: Event) => {
      const { nodeIds } = (e as CustomEvent).detail as { nodeIds: string[] };
      const idSet = new Set(nodeIds);
      r().reactFlowInstance.setNodes((nds) =>
        nds.map((n) => ({ ...n, selected: idSet.has(n.id) })),
      );
    };

    const onOpenQuickChat = (e: Event) => {
      const { nodeId } = (e as CustomEvent).detail as { nodeId: string };
      if (nodeId) r().openChat(nodeId);
    };

    // canvas:widget-open-quick-chat — dispatched by widgets (e.g. Retro Board
    // summarize button) to open Quick Chat at a given screen position with an
    // optional pre-filled message.
    const onWidgetOpenQuickChat = (e: Event) => {
      const { screenX, screenY, initialContent } = (e as CustomEvent)
        .detail as {
        screenX: number;
        screenY: number;
        initialContent?: string;
      };
      r().handleStartQuickChat?.({ x: screenX, y: screenY }, initialContent);
    };

    const onAddEntity = (e: Event) => {
      const { entity } = (e as CustomEvent).detail as { entity: Entity };
      const handleSelect = r().handleSelectEntityRef.current;
      if (!entity || !handleSelect) return;
      handleSelect(entity);
    };

    const onHighlightNodes = (e: Event) => {
      const { nodeIds } = (e as CustomEvent).detail as {
        nodeIds: string[] | null;
      };
      r().setHighlightedNodeIds(nodeIds);
    };

    const onOpenChat = async (e: Event) => {
      const { canvasId: cid, composerDraft } = (e as CustomEvent).detail as {
        canvasId?: string;
        composerDraft?: string;
      };
      if (!cid || cid !== r().canvasId) return;
      await r().ensureCanvasAgentChatOpen?.(composerDraft);
    };

    window.addEventListener("canvas:open-session-pane", onOpenSessionPane);
    window.addEventListener("canvas:delete-section", onDeleteSection);
    window.addEventListener("canvas:edge-label-change", onEdgeLabelChange);
    window.addEventListener("canvas:remove-node", onRemoveNode);
    window.addEventListener("canvas:set-node-hidden", onSetNodeHidden);
    window.addEventListener("canvas:focus-node", onFocusNode);
    window.addEventListener("canvas:focus-point", onFocusPoint);
    window.addEventListener(CANVAS_FOCUS_NODES_EVENT, onFocusNodes);
    window.addEventListener(CANVAS_EXPAND_NODE_EVENT, onExpandNode);
    window.addEventListener("canvas:select-nodes", onSelectNodes);
    window.addEventListener("canvas:open-quick-chat", onOpenQuickChat);
    window.addEventListener(
      "canvas:widget-open-quick-chat",
      onWidgetOpenQuickChat,
    );
    window.addEventListener("canvas:add-entity", onAddEntity);
    window.addEventListener("canvas:highlight-nodes", onHighlightNodes);
    window.addEventListener(CANVAS_OPEN_CHAT_EVENT, onOpenChat);

    return () => {
      window.removeEventListener("canvas:open-session-pane", onOpenSessionPane);
      window.removeEventListener("canvas:delete-section", onDeleteSection);
      window.removeEventListener("canvas:edge-label-change", onEdgeLabelChange);
      window.removeEventListener("canvas:remove-node", onRemoveNode);
      window.removeEventListener("canvas:set-node-hidden", onSetNodeHidden);
      window.removeEventListener("canvas:focus-node", onFocusNode);
      window.removeEventListener("canvas:focus-point", onFocusPoint);
      window.removeEventListener(CANVAS_FOCUS_NODES_EVENT, onFocusNodes);
      window.removeEventListener(CANVAS_EXPAND_NODE_EVENT, onExpandNode);
      window.removeEventListener("canvas:select-nodes", onSelectNodes);
      window.removeEventListener("canvas:open-quick-chat", onOpenQuickChat);
      window.removeEventListener(
        "canvas:widget-open-quick-chat",
        onWidgetOpenQuickChat,
      );
      window.removeEventListener("canvas:add-entity", onAddEntity);
      window.removeEventListener("canvas:highlight-nodes", onHighlightNodes);
      window.removeEventListener(CANVAS_OPEN_CHAT_EVENT, onOpenChat);
    };
  }, []);
}
